import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { themeFromColorType } from '../types/BusinessColorTypes'

const THEME_CACHE_PREFIX = 'shops-customer-shop-theme:'

function themeCacheKey(business) {
  return `${THEME_CACHE_PREFIX}${business}`
}

function applyTheme(theme) {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-theme', theme)
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) {
    meta.setAttribute('content', theme === 'light' ? '#f4f4f5' : '#121212')
  }
}

function readCachedTheme(business) {
  if (!business) return null
  try {
    const value = sessionStorage.getItem(themeCacheKey(business))
    if (value === 'light' || value === 'dark') return value
  } catch {
    /* ignore */
  }
  return null
}

export function rememberBusinessTheme(business, colorType) {
  const theme = themeFromColorType(colorType)
  if (!business) return theme
  try {
    sessionStorage.setItem(themeCacheKey(business), theme)
  } catch {
    /* ignore */
  }
  return theme
}

const ThemeContext = createContext({
  theme: 'dark',
  isLight: false,
  setTheme: () => {},
  applyBusinessColorType: () => {},
})

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    applyTheme('dark')
    return 'dark'
  })

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  const setTheme = useCallback((next) => {
    setThemeState(next === 'light' ? 'light' : 'dark')
  }, [])

  const applyBusinessColorType = useCallback((business, colorType) => {
    const next = rememberBusinessTheme(business, colorType)
    setThemeState(next)
  }, [])

  const applyCachedBusinessTheme = useCallback((business) => {
    const cached = readCachedTheme(business)
    if (cached) setThemeState(cached)
  }, [])

  const value = useMemo(
    () => ({
      theme,
      isLight: theme === 'light',
      setTheme,
      applyBusinessColorType,
      applyCachedBusinessTheme,
    }),
    [theme, setTheme, applyBusinessColorType, applyCachedBusinessTheme]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  return useContext(ThemeContext)
}

export default ThemeContext
